import {
  collection,
  deleteField,
  doc,
  GeoPoint,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  pickOverrides,
  POST_ANALYSIS_OVERRIDE_KEYS,
  type DistrictAiAnalysisConfig,
  type PostAnalysisDef,
} from '@citizen-walk/shared-types';
import { useEffect, useState } from 'react';
import {
  getModeTemplate,
  MODE_PROFILES_COLLECTION,
  parseModeProfile,
  type ColorRule,
  type District,
  type DistrictMode,
  type MapDownloadPolicy,
  type ModeProfile,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';
import { useAdminAuth } from '../auth';

/**
 * 地区コレクション read hook (Phase 5.3 R-33 step 3、admin DistrictDetailPage 前提)。
 *
 * 旧 admin districts list を踏襲、ただし Phase 5.3 では PMTiles 生成 UI 接続が目的のため
 * read-only。CRUD (write / archive / token rotation) は Phase 5 admin sprint で別途実装。
 *
 * 認可 (auth-aware):
 *   - master: 全地区を collection list (rules: master は canAccessDistrict 全 true)。
 *   - district admin: 担当地区 (claim `districts`) のみ doc 個別購読。全 list は
 *     accessibleDistricts を持たない地区管理者だと rules で拒否されるため、
 *     担当地区を 1 件ずつ subscribe して集約する (rules の isDistrictAdmin read allow が前提)。
 *   - role なし: 空一覧。
 */

export type DistrictsState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: District[]; error: null }
  | { status: 'error'; items: []; error: Error };

function mapDistrictDoc(snap: { id: string; data: () => DocumentData }): District {
  const data = snap.data();
  return {
    ...(data as Omit<District, 'id'>),
    id: snap.id,
  };
}

export function useDistricts(): DistrictsState {
  const { state: auth } = useAdminAuth();
  const [state, setState] = useState<DistrictsState>({
    status: 'loading',
    items: [],
    error: null,
  });

  // role / 担当地区を effect の依存に使えるよう安定化 (配列は join key 化)。
  const role = auth.role;
  const myDistrictsKey = role === 'district' ? auth.districts.join(',') : '';

  useEffect(() => {
    // 認証状態が確定するまで loading のまま待つ (claim 未読込で空配列を確定させない)。
    if (!auth.ready) return;

    // master: 全地区を list (firestore.rules: master は canAccessDistrict 全 true)。
    if (role === 'master') {
      const q = query(collection(db, 'districts'), orderBy('name'));
      const unsub: Unsubscribe = onSnapshot(
        q,
        (snap) => {
          const items: District[] = snap.docs.map(mapDistrictDoc);
          setState({ status: 'ready', items, error: null });
        },
        (error) => setState({ status: 'error', items: [], error }),
      );
      return () => unsub();
    }

    // district admin: 担当地区を 1 件ずつ doc 購読する。
    // 全 list (collection query) は accessibleDistricts claim を持たない地区管理者だと
    // rules で拒否されるため、担当地区の doc を個別 get/subscribe して集約する
    // (単一 doc read は isDistrictAdmin allow で許可される)。
    if (role === 'district') {
      const ids = myDistrictsKey ? myDistrictsKey.split(',') : [];
      if (ids.length === 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setState({ status: 'ready', items: [], error: null });
        return;
      }
      const byId = new Map<string, District>();
      const unsubs: Unsubscribe[] = ids.map((id) =>
        onSnapshot(
          doc(db, 'districts', id),
          (snap) => {
            if (snap.exists()) {
              byId.set(
                id,
                mapDistrictDoc({ id: snap.id, data: () => snap.data() as DocumentData }),
              );
            } else {
              byId.delete(id);
            }
            const items = [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
            setState({ status: 'ready', items, error: null });
          },
          (error) => setState({ status: 'error', items: [], error }),
        ),
      );
      return () => unsubs.forEach((u) => u());
    }

    // 権限なし (role=null): 空一覧。
    setState({ status: 'ready', items: [], error: null });
    return;
  }, [auth.ready, role, myDistrictsKey]);

  return state;
}

export type DistrictState =
  | { status: 'loading'; district: null; error: null }
  | { status: 'ready'; district: District; error: null }
  | { status: 'not-found'; district: null; error: null }
  | { status: 'error'; district: null; error: Error };

/**
 * 地区 CRUD helper (admin 第 10 弾、Phase 5.4)。
 *
 * 旧 apps/admin/src/pages/DistrictsPage.tsx + DistrictDetailPage.tsx の write 部分を踏襲。
 * write は firestore.rules:46 `if isMaster()` で master role のみ許可 (rules 既存)。
 *
 * 踏襲した点 (旧 v1):
 *   - 32-byte hex token (`crypto.getRandomValues` 16 bytes → hex 文字列、旧 generateToken)
 *   - id は crypto.randomUUID (旧 line 80)
 *   - 新規時の固定 default: `mapDownloadPolicy='manual'` / `analysisEnabled=true`
 *   - archive = `archivedAt: serverTimestamp()` (旧 line 97 で `archivedAt: null` 初期化 → 後で archive)
 *   - postAreaCenter は GeoPoint (lat/lng)、postAreaRadiusM は number (m)
 *
 * 変えた点 (v2):
 *   - 旧の `dataSettings` / `mapDisplay` / `userProfileOptions` の getModeTemplate(mode) 初期化は
 *     scope 外 (本 sprint では基本 fields のみ、入れ子は後続 sprint or admin 編集 UI で個別追加)
 *   - 旧の `createdBy: user.uid` を呼出側で渡す形に統一 (master の uid 必須)
 *
 * 廃止した点:
 *   - 旧の `analysisSiteUrl: null` / `surveyUrl: null` 初期化 (現 schema では optional、未指定 OK)
 */

const URL_TOKEN_BYTES = 16;

/**
 * Firestore へ書き込む直前に payload から `undefined` 値を再帰的に除去する。
 *
 * Firestore の setDoc は `undefined` フィールド値を許容せず
 * 「Unsupported field value: undefined」で reject する。ModeProfile スナップショット
 * (dataSettings.userFields/postFields の FieldDef optional) 等、ネストした undefined が
 * 混入しても安全に書けるようにするための防御。
 *
 * **プレーン object と配列のみ再帰**し、GeoPoint / FieldValue(serverTimestamp) / Date 等の
 * クラスインスタンスは触らない (constructor が Object 以外は素通し)。null は保持 (意図的なクリア)。
 */
export function pruneUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => pruneUndefinedDeep(v)) as unknown as T;
  }
  if (
    value !== null &&
    typeof value === 'object' &&
    ((value as object).constructor === Object || (value as object).constructor === undefined)
  ) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = pruneUndefinedDeep(v);
    }
    return out as unknown as T;
  }
  return value;
}

export function generateUrlToken(): string {
  const bytes = new Uint8Array(URL_TOKEN_BYTES);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface CreateDistrictInput {
  /** 新規 id (省略時 `crypto.randomUUID()`)。 */
  id?: string;
  name: string;
  mode: DistrictMode;
  /** custom モード (B2) の利用者表示名 (日本語)。組込モードは未指定で可。 */
  modeLabel?: string;
  /** master の uid (createdBy / updatedBy に記録)。 */
  uid: string;
  /** 任意。未指定なら `generateUrlToken()` で 32-byte hex を生成。 */
  urlToken?: string;
  /** 投稿可能エリア中心 (PMTiles 切出 bbox に必須、未指定なら省略され後で編集) */
  postAreaCenter?: { latitude: number; longitude: number };
  postAreaRadiusM?: number;
  mapDownloadPolicy?: MapDownloadPolicy;
  analysisEnabled?: boolean;
  analysisDisplayConfigId?: string;
  /**
   * AI 解析 opt-in flag (Sprint L、Phase 5.5)。
   * `aiAnalysisEnabled` / `aiAnalysisSettingsId` のいずれかが指定されたら nested
   * `aiAnalysis` object として書込む。両方未指定なら `aiAnalysis` キー自体省略
   * (旧 v1 と同じ後方互換 behavior — 未設定 doc は trigger 側で enabled=true 扱い)。
   */
  aiAnalysisEnabled?: boolean;
  /**
   * M1 `aiAnalysisSettings/{id}` への参照 (空文字や null なら master 自動選択)。
   * 文字列で指定されたらそのまま、null/undefined なら settingsId=null (=自動選択) で書く。
   */
  aiAnalysisSettingsId?: string | null;
  /**
   * 地区別 M3 `aiOutputFormats/{id}` 上書き (docs/105 A-③、旧 v1 `outputFormatIdOverride` 踏襲)。
   * 設定時は preset (M1) の outputFormatId より優先。空文字 / null なら override なし
   * (create では key 自体を省略)。`aiAnalysis` nested object にぶら下げる。
   */
  aiOutputFormatIdOverride?: string | null;
  /**
   * AI 検証 opt-in flag (Sprint Y、Phase 5.5)。
   * いずれかの aiVerification* field が指定されたら nested `aiVerification` object として書込む。
   * 全て未指定なら `aiVerification` キー自体省略 (後方互換、未設定 doc は user 側で
   * `enabled=false / allowFreeVerification=false` default 扱い)。
   */
  aiVerificationEnabled?: boolean;
  /** AI 検証で使うアクティブ質問テンプレート ID (`districts/{id}/aiVerificationTemplates/{templateId}` 参照)。 */
  aiVerificationActiveTemplateId?: string | null;
  /** 自由検証 (admin 候補指定なし) の許可。 */
  aiVerificationAllowFree?: boolean;
  /**
   * Sprint BG-DataSettings 30: 新地区作成時に採用する ModeProfile id。
   * 指定があれば Firestore から profile を読み、`dataSettings` / `userProfileOptions` /
   * `profileId` / `profileVersion` および推奨マスタ ID を地区側にコピーする。
   * 未指定なら従来通り profile snapshot コピーをスキップ (modeTemplates fallback は user 側で実行)。
   */
  profileId?: string;
  /**
   * 地図色付け規則の明示指定 (docs/105 A-④)。
   * 未指定なら `getModeTemplate(mode).mapDisplay.colorRule` を初期値として district に
   * コピーする (old2 の「新規地区作成時に colorRule も自動コピー」挙動を復活)。
   * `null` を渡すと colorRule のコピー自体を抑止 (mapDisplay キーを書かない)。
   */
  mapDisplayColorRule?: ColorRule | null;
}

/**
 * 新規地区を作成。`/districts/{id}` を setDoc で書込。
 * 認可: firestore.rules `allow create: if isMaster()`。
 */
export async function createDistrict(input: CreateDistrictInput): Promise<string> {
  const id = input.id ?? crypto.randomUUID();
  const ref = doc(db, 'districts', id);
  const now = serverTimestamp();

  const payload: Record<string, unknown> = {
    id,
    name: input.name.trim(),
    urlToken: input.urlToken ?? generateUrlToken(),
    mode: input.mode,
    createdAt: now,
    createdBy: input.uid,
    updatedAt: now,
    updatedBy: input.uid,
    archivedAt: null,
    schemaVersion: 1,
    mapDownloadPolicy: input.mapDownloadPolicy ?? 'manual',
    analysisEnabled: input.analysisEnabled ?? true,
    // 新規地区はセッション未参加者の投稿を既定で許可する (運用方針)。
    // 後から admin の「その他設定」(DistrictMiscPanel) で変更可。
    allowPostingWithoutSession: true,
  };

  if (input.modeLabel && input.modeLabel.trim()) {
    payload.modeLabel = input.modeLabel.trim();
  }
  if (input.postAreaCenter) {
    payload.postAreaCenter = new GeoPoint(
      input.postAreaCenter.latitude,
      input.postAreaCenter.longitude,
    );
  }
  if (typeof input.postAreaRadiusM === 'number') {
    payload.postAreaRadiusM = input.postAreaRadiusM;
  }
  if (input.analysisDisplayConfigId) {
    payload.analysisDisplayConfigId = input.analysisDisplayConfigId;
  }
  const aiAnalysis = buildAiAnalysisCreate(input);
  if (aiAnalysis !== undefined) {
    payload.aiAnalysis = aiAnalysis;
  }
  const aiVerification = buildAiVerificationCreate(input);
  if (aiVerification !== undefined) {
    payload.aiVerification = aiVerification;
  }

  // docs/105 A-④: 新規地区に初期 colorRule を配布 (old2 の自動コピー挙動を復活)。
  // 明示 colorRule があればそれを、無ければ mode-template の colorRule を district.mapDisplay に書く。
  // `null` 明示の場合のみ mapDisplay コピーを抑止する。
  const mapDisplay = buildMapDisplayCreate(input.mode, input.mapDisplayColorRule);
  if (mapDisplay !== undefined) {
    payload.mapDisplay = mapDisplay;
  }

  // Sprint BG-DataSettings 30: profileId 指定があれば Firestore profile を読み snapshot コピー。
  // 読込失敗 / parse 失敗 / mode 不一致 は throw して create を abort (誤った snapshot を書込まないため)。
  if (input.profileId && input.profileId.trim()) {
    const profile = await fetchModeProfile(input.profileId.trim());
    if (!profile) {
      throw new Error(`ModeProfile '${input.profileId}' が存在しないか不正です`);
    }
    if (profile.mode !== input.mode) {
      throw new Error(
        `ModeProfile.mode (${profile.mode}) と district.mode (${input.mode}) が不一致です`,
      );
    }
    applyProfileToCreatePayload(payload, profile);
  }

  // Firestore は undefined を拒否する。profile スナップショット (FieldDef optional) 等の
  // ネスト undefined を除去してから書く (GeoPoint / serverTimestamp は constructor 判定で温存)。
  await setDoc(ref, pruneUndefinedDeep(payload));
  return id;
}

/**
 * Sprint BG-DataSettings 30: Firestore `modeProfiles/{id}` を 1 件 read + parse。
 * 旧 v1 では存在しない概念 (modeTemplates static 経由)。
 */
async function fetchModeProfile(id: string): Promise<ModeProfile | null> {
  const snap = await getDoc(doc(db, MODE_PROFILES_COLLECTION, id));
  if (!snap.exists()) return null;
  return parseModeProfile(snap.data(), snap.id);
}

/**
 * profile snapshot を新地区の create payload にコピー (pure)。
 *
 * 適用順 (上書きポリシー):
 *   - `dataSettings`: profile 由来で常に上書き (snapshot コピー)
 *   - `userProfileOptions`: profile 由来があれば上書き
 *   - `profileId` / `profileVersion`: 常に記録 (将来 diff 反映用)
 *   - `analysisDisplayConfigId`: profile.recommendedMasters 由来があれば
 *     **既存 payload に値が無い場合のみ** 上書き (form 入力優先)
 *   - `aiAnalysis.settingsId`: 同上 (form 入力 / explicit `aiAnalysisSettingsId` 優先)
 *
 * Pure 関数として export し、unit test 可能にしている。
 */
export function applyProfileToCreatePayload(
  payload: Record<string, unknown>,
  profile: ModeProfile,
): void {
  // dataSettings / userProfileOptions / profileId / profileVersion は profile を正とする。
  payload.dataSettings = {
    userFields: profile.userFields,
    postFields: profile.postFields,
  };
  if (profile.userProfileOptions) {
    payload.userProfileOptions = profile.userProfileOptions;
  }
  // custom モード (B2) の利用者表示名を地区へ転写 (getModeLabel の customLabel として使う)。
  // form 入力 (= payload に既に値あり) を優先する保守的設計 (推奨マスタと同方針)。
  if (profile.modeLabel && payload.modeLabel === undefined) {
    payload.modeLabel = profile.modeLabel;
  }
  payload.profileId = profile.id;
  payload.profileVersion = profile.profileVersion;

  const rec = profile.recommendedMasters;
  if (!rec) return;

  // 推奨マスタ ID は form 入力 (= payload に既に値あり) を優先する保守的設計。
  if (rec.displayConfigId && payload.analysisDisplayConfigId === undefined) {
    payload.analysisDisplayConfigId = rec.displayConfigId;
  }
  if (rec.analysisSettingsId) {
    const cur = payload.aiAnalysis as { enabled?: boolean; settingsId?: string | null } | undefined;
    if (!cur) {
      payload.aiAnalysis = { enabled: true, settingsId: rec.analysisSettingsId };
    } else if (cur.settingsId === undefined || cur.settingsId === null) {
      payload.aiAnalysis = { ...cur, settingsId: rec.analysisSettingsId };
    }
  }
}

/**
 * create 時の `aiAnalysis` nested object 組立 (pure、unit test 容易化のため切出し)。
 *
 * 戻り値:
 *   - `undefined`: `aiAnalysisEnabled` / `aiAnalysisSettingsId` /
 *     `aiOutputFormatIdOverride` いずれも未指定 → payload に `aiAnalysis` キー自体含めない
 *     (旧 v1 後方互換、未設定 doc は trigger 側で enabled=true 扱い)。
 *   - `{ enabled, settingsId, outputFormatIdOverride? }`: いずれか指定あり →
 *     enabled は未指定なら true デフォルト、settingsId は空文字 trim 後の文字列 or null。
 *     `outputFormatIdOverride` (docs/105 A-③) は trim 後の非空文字列があるときのみ含める
 *     (空 / null / 未指定なら key 自体省略 = 上書きなし、payload を最小化)。
 */
export function buildAiAnalysisCreate(input: {
  aiAnalysisEnabled?: boolean;
  aiAnalysisSettingsId?: string | null;
  aiOutputFormatIdOverride?: string | null;
}): { enabled: boolean; settingsId: string | null; outputFormatIdOverride?: string } | undefined {
  if (
    input.aiAnalysisEnabled === undefined &&
    input.aiAnalysisSettingsId === undefined &&
    input.aiOutputFormatIdOverride === undefined
  ) {
    return undefined;
  }
  const enabled = input.aiAnalysisEnabled ?? true;
  let settingsId: string | null;
  if (input.aiAnalysisSettingsId === undefined || input.aiAnalysisSettingsId === null) {
    settingsId = null;
  } else {
    const trimmed = input.aiAnalysisSettingsId.trim();
    settingsId = trimmed === '' ? null : trimmed;
  }
  const out: { enabled: boolean; settingsId: string | null; outputFormatIdOverride?: string } = {
    enabled,
    settingsId,
  };
  // docs/105 A-③: override は非空のときのみ含める (空 → 上書きなし、key 省略)。
  const overrideTrim =
    typeof input.aiOutputFormatIdOverride === 'string' ? input.aiOutputFormatIdOverride.trim() : '';
  if (overrideTrim) {
    out.outputFormatIdOverride = overrideTrim;
  }
  return out;
}

/**
 * create 時の `mapDisplay` nested object 組立 (docs/105 A-④、pure)。
 *
 * 戻り値:
 *   - `undefined`: colorRule=null 明示 (コピー抑止)、または mode-template に colorRule が無い場合
 *     → payload に `mapDisplay` キーを含めない。
 *   - `{ colorRule }`: 明示 colorRule、または `getModeTemplate(mode).mapDisplay.colorRule` を
 *     district 初期値として書込む (old2 の新規地区作成時 colorRule 自動コピー挙動を復活)。
 *
 * 【廃止予定 fallback】`getModeTemplate` (@deprecated) は MD-3 (2026-07-24) 以降、新規地区が
 * ModeProfile 必須になったため実質 legacy 経路。ModeProfile 側に mapDisplay.colorRule を
 * 持たせる拡張 (issue 化予定) が入れば撤去可能。custom mode は BOUSAI_TEMPLATE に落ちる点に注意。
 *
 * mode-template の colorRule は static 定数なので read 失敗の懸念は無い。explicit 引数が
 * undefined のときのみ template fallback を引く (form 入力 / 明示指定を優先)。
 */
export function buildMapDisplayCreate(
  mode: DistrictMode,
  explicit: ColorRule | null | undefined,
): { colorRule: ColorRule } | undefined {
  if (explicit === null) return undefined;
  const colorRule = explicit ?? getModeTemplate(mode).mapDisplay?.colorRule;
  if (!colorRule) return undefined;
  return { colorRule };
}

/**
 * create 時の `aiVerification` nested object 組立 (Sprint Y、pure)。
 *
 * 戻り値:
 *   - `undefined`: 3 field 全て未指定 → payload に `aiVerification` キー含めない (後方互換)
 *   - `{ enabled, activeTemplateId, allowFreeVerification }`: いずれか指定あり →
 *     - `enabled` 未指定なら false デフォルト
 *     - `activeTemplateId` は trim 後の値 / 空文字や undefined / null は null
 *     - `allowFreeVerification` 未指定なら false デフォルト
 *
 * Sprint L の `buildAiAnalysisCreate` と同 pattern (sub-field 全 undefined → 親キー省略)。
 */
export function buildAiVerificationCreate(input: {
  aiVerificationEnabled?: boolean;
  aiVerificationActiveTemplateId?: string | null;
  aiVerificationAllowFree?: boolean;
}):
  | { enabled: boolean; activeTemplateId: string | null; allowFreeVerification: boolean }
  | undefined {
  if (
    input.aiVerificationEnabled === undefined &&
    input.aiVerificationActiveTemplateId === undefined &&
    input.aiVerificationAllowFree === undefined
  ) {
    return undefined;
  }
  const enabled = input.aiVerificationEnabled ?? false;
  let activeTemplateId: string | null;
  if (
    input.aiVerificationActiveTemplateId === undefined ||
    input.aiVerificationActiveTemplateId === null
  ) {
    activeTemplateId = null;
  } else {
    const trimmed = input.aiVerificationActiveTemplateId.trim();
    activeTemplateId = trimmed === '' ? null : trimmed;
  }
  const allowFreeVerification = input.aiVerificationAllowFree ?? false;
  return { enabled, activeTemplateId, allowFreeVerification };
}

export interface UpdateDistrictInput {
  id: string;
  uid: string;
  name?: string;
  mode?: DistrictMode;
  /** custom モード (B2) の利用者表示名。string で書込、null で明示クリア、undefined で温存。 */
  modeLabel?: string | null;
  urlToken?: string;
  postAreaCenter?: { latitude: number; longitude: number } | null;
  postAreaRadiusM?: number | null;
  mapDownloadPolicy?: MapDownloadPolicy;
  analysisEnabled?: boolean;
  analysisDisplayConfigId?: string | null;
  /**
   * AI 解析 opt-in flag (Sprint L、Phase 5.5)。
   * undefined なら既存値温存、boolean 値なら `aiAnalysis.enabled` を merge 上書き。
   */
  aiAnalysisEnabled?: boolean;
  /**
   * M1 `aiAnalysisSettings/{id}` への参照 (Sprint L)。
   * - undefined: 既存値温存 (`aiAnalysis.settingsId` を上書きしない)
   * - null: 明示クリア (`aiAnalysis.settingsId: null` を merge)
   * - string: 値書込 (空文字は呼出側で null に変換しておく)
   */
  aiAnalysisSettingsId?: string | null;
  /**
   * 地区別 M3 出力フォーマット上書き (docs/105 A-③、旧 v1 `outputFormatIdOverride` 踏襲)。
   * - undefined: 既存値温存 (`aiAnalysis.outputFormatIdOverride` を上書きしない)
   * - null: 明示クリア (`aiAnalysis.outputFormatIdOverride: null` を merge)
   * - string: 値書込 (空文字は呼出側で null に変換しておく)
   */
  aiOutputFormatIdOverride?: string | null;
  /**
   * AI 検証 opt-in flag (Sprint Y)。
   * undefined なら既存値温存、boolean なら `aiVerification.enabled` を merge 上書き。
   */
  aiVerificationEnabled?: boolean;
  /**
   * AI 検証で使うアクティブ質問テンプレート ID (Sprint Y)。
   * - undefined: 既存値温存
   * - null: 明示クリア (`aiVerification.activeTemplateId: null` を merge)
   * - string: 値書込 (空文字は null に正規化)
   */
  aiVerificationActiveTemplateId?: string | null;
  /**
   * 自由検証許可 (Sprint Y)。
   * undefined なら既存値温存、boolean なら `aiVerification.allowFreeVerification` を merge 上書き。
   */
  aiVerificationAllowFree?: boolean;
}

/**
 * 既存地区を update。指定された field のみ merge 書込み。
 * 認可: firestore.rules `allow update: if isMaster()`。
 *
 * null を渡すと該当 field を削除 (Firestore deleteField sentinel は使わず、明示的に
 * 上位 field のみ null 化する設計。Phase 5 で完全な編集 UI を作る際に再考)。
 */
export async function updateDistrict(input: UpdateDistrictInput): Promise<void> {
  const ref = doc(db, 'districts', input.id);
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
    updatedBy: input.uid,
  };
  if (input.name !== undefined) payload.name = input.name.trim();
  if (input.mode !== undefined) payload.mode = input.mode;
  if (input.modeLabel !== undefined) {
    payload.modeLabel = input.modeLabel === null ? null : input.modeLabel.trim() || null;
  }
  if (input.urlToken !== undefined) payload.urlToken = input.urlToken;
  if (input.mapDownloadPolicy !== undefined) payload.mapDownloadPolicy = input.mapDownloadPolicy;
  if (input.analysisEnabled !== undefined) payload.analysisEnabled = input.analysisEnabled;
  if (input.analysisDisplayConfigId !== undefined) {
    payload.analysisDisplayConfigId = input.analysisDisplayConfigId;
  }
  if (input.postAreaCenter !== undefined) {
    payload.postAreaCenter =
      input.postAreaCenter === null
        ? null
        : new GeoPoint(input.postAreaCenter.latitude, input.postAreaCenter.longitude);
  }
  if (input.postAreaRadiusM !== undefined) {
    payload.postAreaRadiusM = input.postAreaRadiusM;
  }
  const aiAnalysis = buildAiAnalysisUpdate(input);
  if (aiAnalysis !== undefined) {
    payload.aiAnalysis = aiAnalysis;
  }
  const aiVerification = buildAiVerificationUpdate(input);
  if (aiVerification !== undefined) {
    payload.aiVerification = aiVerification;
  }

  await setDoc(ref, payload, { merge: true });
}

/**
 * update 時の `aiAnalysis` nested object 組立 (pure、unit test 容易化のため切出し)。
 *
 * Firestore の merge: true により nested object も field 単位で merge される。
 *   - `aiAnalysisEnabled` 指定あり / `aiAnalysisSettingsId` 未指定: `{ enabled }` のみ merge
 *     → 既存 `settingsId` 温存
 *   - `aiAnalysisSettingsId: null` 明示: `{ settingsId: null }` で明示クリア
 *   - `aiAnalysisSettingsId: 'xxx'`: trim 後の値 / 空文字は null
 *   - `aiOutputFormatIdOverride` (docs/105 A-③): null / 空文字なら `{ outputFormatIdOverride: null }`
 *     で明示クリア、文字列なら trim 後の値を merge、undefined なら既存値温存
 *   - 全 field undefined: `undefined` を返し payload に `aiAnalysis` キー含めない
 *
 * 注: `aiAnalysis` 全体を一度に置き換えるのではなく、指定された sub-key のみを書く。
 * Firestore の `setDoc(..., { merge: true })` は nested object も再帰 merge するため、
 * 部分書込で既存値温存が成立する。
 */
export function buildAiAnalysisUpdate(input: {
  aiAnalysisEnabled?: boolean;
  aiAnalysisSettingsId?: string | null;
  aiOutputFormatIdOverride?: string | null;
}):
  | { enabled?: boolean; settingsId?: string | null; outputFormatIdOverride?: string | null }
  | undefined {
  if (
    input.aiAnalysisEnabled === undefined &&
    input.aiAnalysisSettingsId === undefined &&
    input.aiOutputFormatIdOverride === undefined
  ) {
    return undefined;
  }
  const out: {
    enabled?: boolean;
    settingsId?: string | null;
    outputFormatIdOverride?: string | null;
  } = {};
  if (input.aiAnalysisEnabled !== undefined) {
    out.enabled = input.aiAnalysisEnabled;
  }
  if (input.aiAnalysisSettingsId !== undefined) {
    if (input.aiAnalysisSettingsId === null) {
      out.settingsId = null;
    } else {
      const trimmed = input.aiAnalysisSettingsId.trim();
      out.settingsId = trimmed === '' ? null : trimmed;
    }
  }
  if (input.aiOutputFormatIdOverride !== undefined) {
    if (input.aiOutputFormatIdOverride === null) {
      out.outputFormatIdOverride = null;
    } else {
      const trimmed = input.aiOutputFormatIdOverride.trim();
      out.outputFormatIdOverride = trimmed === '' ? null : trimmed;
    }
  }
  return out;
}

/**
 * update 時の `aiVerification` nested object 組立 (Sprint Y、pure)。
 *
 * Sprint L の `buildAiAnalysisUpdate` と同 pattern — 指定された sub-key のみ payload に含め
 * Firestore の `setDoc(..., { merge: true })` で nested object を field 単位 merge する。
 *
 *   - 3 field 全 undefined: `undefined` を返し payload に `aiVerification` キー含めない (既存値温存)
 *   - `aiVerificationEnabled` のみ指定: `{ enabled }` のみ書く (他は既存温存)
 *   - `aiVerificationActiveTemplateId: null`: `{ activeTemplateId: null }` で明示クリア
 *   - `aiVerificationActiveTemplateId: 'xxx'`: trim 後の値 / 空文字は null
 *   - `aiVerificationAllowFree` 指定: `{ allowFreeVerification }` を merge
 */
export function buildAiVerificationUpdate(input: {
  aiVerificationEnabled?: boolean;
  aiVerificationActiveTemplateId?: string | null;
  aiVerificationAllowFree?: boolean;
}):
  | { enabled?: boolean; activeTemplateId?: string | null; allowFreeVerification?: boolean }
  | undefined {
  if (
    input.aiVerificationEnabled === undefined &&
    input.aiVerificationActiveTemplateId === undefined &&
    input.aiVerificationAllowFree === undefined
  ) {
    return undefined;
  }
  const out: {
    enabled?: boolean;
    activeTemplateId?: string | null;
    allowFreeVerification?: boolean;
  } = {};
  if (input.aiVerificationEnabled !== undefined) {
    out.enabled = input.aiVerificationEnabled;
  }
  if (input.aiVerificationActiveTemplateId !== undefined) {
    if (input.aiVerificationActiveTemplateId === null) {
      out.activeTemplateId = null;
    } else {
      const trimmed = input.aiVerificationActiveTemplateId.trim();
      out.activeTemplateId = trimmed === '' ? null : trimmed;
    }
  }
  if (input.aiVerificationAllowFree !== undefined) {
    out.allowFreeVerification = input.aiVerificationAllowFree;
  }
  return out;
}

/**
 * 地区の `aiAnalysis` (DistrictAiAnalysisConfig) を **全置換** で保存 (Phase 4 統合エディタ地区モード)。
 *
 * `updateDistrict` の `buildAiAnalysisUpdate` は merge:true による sub-key 単位 merge で「既存値温存」
 * を実現するため、override OFF にした項目 (= config から落とした項目) が残骸として残ってしまう。
 * 統合エディタ地区モードは「override ON の項目だけを詰めた config 全体」で aiAnalysis を置換したいので、
 * `updateDoc` で `aiAnalysis` を 1 フィールドとして丸ごと上書きする (nested map 全置換 = OFF キーは消える)。
 *
 * 認可: firestore.rules `allow update: if isMaster()` (既存)。
 * Firestore は undefined を拒否するため、呼出側は buildDistrictConfigPayload で undefined を排除済み。
 */
export async function setDistrictAiAnalysis(
  id: string,
  uid: string,
  config: DistrictAiAnalysisConfig,
): Promise<void> {
  const ref = doc(db, 'districts', id);
  await updateDoc(ref, {
    ...buildEditorAiAnalysisUpdate(config, deleteField()),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  });
}

/**
 * 追加の観点 (docs/29 §7-1) の地区の上書きを保存する。最新の `postAnalyses` を読み、指定の観点の上書きの項目
 * だけを置き換えて配列ごと書く (key・表示名・解析設定と、ほかの観点はそのまま)。
 */
export async function setDistrictPostAnalysisOverrides(
  id: string,
  uid: string,
  key: string,
  config: DistrictAiAnalysisConfig,
): Promise<void> {
  const ref = doc(db, 'districts', id);
  const snap = await getDoc(ref);
  const current = (snap.data()?.aiAnalysis as DistrictAiAnalysisConfig | undefined)?.postAnalyses;
  const next = replacePostAnalysisOverrides(current, key, config);
  await updateDoc(ref, {
    'aiAnalysis.postAnalyses': pruneUndefinedDeep(next),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  });
}

/** 観点の上書きの置き換え (pure)。見つからない観点は Error。 */
export function replacePostAnalysisOverrides(
  list: PostAnalysisDef[] | null | undefined,
  key: string,
  config: DistrictAiAnalysisConfig,
): PostAnalysisDef[] {
  const arr = Array.isArray(list) ? list : [];
  if (!arr.some((a) => a?.key === key)) throw new Error(`観点が見つかりません: ${key}`);
  return arr.map((a) => {
    if (a?.key !== key) return a;
    const base: Record<string, unknown> = { ...a };
    for (const k of POST_ANALYSIS_OVERRIDE_KEYS) delete base[k];
    return { ...(base as unknown as PostAnalysisDef), ...pickOverrides(config) };
  });
}

/**
 * 統合エディタ地区モードが扱う `aiAnalysis` の項目 (buildDistrictConfigPayload が組み立てるもの)。
 * これ以外 (`legacyModeResults` / 机上調査の `streetSurveys` / `streetSurveyTemplateId` /
 * `streetSurveyOverride` / 解析の観点 `postAnalyses` / `defaultAnalysisLabel` / `lastRunAt` 等) は
 * ほかの画面・処理が持つので、エディタの保存で消さない (docs/29 レビュー 🔴、2026-10-15)。
 */
export const EDITOR_MANAGED_AI_ANALYSIS_KEYS = [
  'enabled',
  'settingsId',
  'outputFormatIdOverride',
  'promptSectionOverrides',
  'outputFieldsOverride',
  'inputFieldKeys',
  'generationConfigOverride',
  'categoryMasterIdsOverride',
  'titleGenerationOverride',
  'validatorsOverride',
  'promptInputMappingOverride',
] as const;

/**
 * エディタの保存用の更新 (pure)。エディタが扱う項目だけを、config にあれば置き換え・無ければ消す
 * (OFF にした上書きの残骸を残さない、従来の全置換と同じ結果)。それ以外の項目には触らない。
 */
export function buildEditorAiAnalysisUpdate<T>(
  config: DistrictAiAnalysisConfig,
  deleteSentinel: T,
): Record<string, unknown> {
  const cleaned = pruneUndefinedDeep(config) as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of EDITOR_MANAGED_AI_ANALYSIS_KEYS) {
    out[`aiAnalysis.${key}`] = key in cleaned ? cleaned[key] : deleteSentinel;
  }
  return out;
}

/**
 * 地区を archive (`archivedAt = serverTimestamp()` 論理削除)。
 * firestore.rules `allow delete: if false` のため物理削除は不可。
 */
export async function archiveDistrict(id: string, uid: string): Promise<void> {
  const ref = doc(db, 'districts', id);
  await setDoc(
    ref,
    {
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
}

/**
 * 地区の archive を解除 (`archivedAt = null`)。
 */
export async function unarchiveDistrict(id: string, uid: string): Promise<void> {
  const ref = doc(db, 'districts', id);
  await setDoc(
    ref,
    {
      archivedAt: null,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
}

/**
 * urlToken を再発行。漏洩時の対応用 (旧 admin 同名機能踏襲)。
 * 既存 token を持つ URL は無効化される (FIFO Custom Claims `accessibleDistricts` は revoke されない
 * 点に注意 — 完全な claims revoke は別 sprint)。
 */
export async function regenerateUrlToken(id: string, uid: string): Promise<string> {
  const token = generateUrlToken();
  const ref = doc(db, 'districts', id);
  await setDoc(
    ref,
    {
      urlToken: token,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
  return token;
}

/** 単一地区 onSnapshot hook。`districts/{districtId}` の生 doc を返す。 */
export function useDistrict(districtId: string | null): DistrictState {
  const [state, setState] = useState<DistrictState>({
    status: 'loading',
    district: null,
    error: null,
  });
  useEffect(() => {
    if (!districtId) {
      // 既存挙動 (districtId 欠落時の即時 not-found)。external state (Firestore) 同期 effect の
      // 早期 return パスで、cascading render を起こさない (Phase 4 で本ファイルを lint 対象化したため明示)。
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({ status: 'not-found', district: null, error: null });
      return;
    }
    const ref = doc(db, 'districts', districtId);
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setState({ status: 'not-found', district: null, error: null });
          return;
        }
        setState({
          status: 'ready',
          district: mapDistrictDoc({ id: snap.id, data: () => snap.data()! }),
          error: null,
        });
      },
      (error) => setState({ status: 'error', district: null, error }),
    );
    return () => unsub();
  }, [districtId]);
  return state;
}
